<template>
  <div class="prompt-container">
    <h1 class="prompt-title">AI Agents Management</h1>
    <v-select
      v-model="selectedPurpose"
      :items="purposeOptions"
      label="Select Agent"
      outlined
      class="prompt-select"
    ></v-select>

    <v-select
      v-model="selectedClientId"
      :items="clientOptions"
      item-title="client_name"
      item-value="client_id"
      label="Select Client"
      outlined
      class="prompt-select"
      @update:modelValue="onClientChange"
      :disabled="!selectedPurpose"
    ></v-select>
    <v-textarea
      v-model="editablePrompt"
      label="Edit Prompt"
      rows="10"
      outlined
      class="prompt-textarea"
      :disabled="!selectedClientId"
    ></v-textarea>
    <v-btn
      color="primary"
      class="mt-3 prompt-save-btn"
      @click="savePrompt"
      :disabled="!selectedClientId"
    >
      Save Prompt
    </v-btn>

    <h2 class="tools-title">{{ selectedPurpose }} Tools</h2>
    <!-- Error Dialog -->
    <v-dialog v-model="showErrorDialog" max-width="500">
      <v-card>
        <v-card-title class="text-error">
          <v-icon start icon="mdi-alert-circle" color="error" class="mr-2"></v-icon>
          Error
        </v-card-title>
        <v-card-text class="pt-4">
          {{ toolErrorMessage }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            variant="text"
            @click="showErrorDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <v-card class="mb-6 pa-4">
      <h3 class="text-h6 mb-4">Create New Tool</h3>
      <v-form @submit.prevent="createTool">
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="newTool.name"
              label="Name"
              required
              density="comfortable"
              variant="outlined"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="newTool.description"
              label="Description"
              required
              density="comfortable"
              variant="outlined"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="newTool.run_location"
              :items="locationOptions"
              label="Run Location"
              required
              density="comfortable"
              variant="outlined"
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="10">
            <v-textarea
              v-model="newTool.sql_query"
              label="SQL Query"
              rows="3"
              required
              variant="outlined"
              density="comfortable"
            ></v-textarea>
          </v-col>
          <v-col cols="12" md="2" class="d-flex align-center">
            <v-switch
              v-model="newTool.active"
              label="Active"
              inset
              color="success"
            ></v-switch>
          </v-col>
        </v-row>

        <v-expansion-panels class="mt-4">
          <v-expansion-panel>
            <v-expansion-panel-title>Parameters</v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-row>
                <v-col cols="12" class="d-flex justify-end mb-2">
                  <v-btn
                    color="primary"
                    size="small"
                    @click="addNewToolParameter"
                    prepend-icon="mdi-plus"
                  >
                    Add Parameter
                  </v-btn>
                </v-col>
                <v-col cols="12" v-for="(param, index) in newTool.parameters" :key="index">
                  <v-card variant="outlined" class="pa-3 mb-2">
                    <v-row dense>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="param.name"
                          label="Name*"
                          required
                          density="comfortable"
                          variant="outlined"
                          hide-details
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="param.datatype"
                          :items="datatypeOptions"
                          label="Data Type*"
                          required
                          density="comfortable"
                          variant="outlined"
                          hide-details
                        ></v-select>
                      </v-col>
                      <v-col cols="12" md="4" class="d-flex align-center">
                        <v-text-field
                          v-model="param.description"
                          label="Description"
                          density="comfortable"
                          variant="outlined"
                          hide-details
                        ></v-text-field>
                        <v-btn
                          icon="mdi-delete"
                          color="error"
                          size="small"
                          class="ml-2 shrink"
                          @click="removeNewToolParameter(index)"
                        ></v-btn>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>
              </v-row>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <div class="d-flex justify-end mt-4">
          <v-btn color="error" @click="resetNewTool" text class="mr-2">Reset</v-btn>
          <v-btn color="success" type="submit">Create Tool</v-btn>
        </div>
      </v-form>
    </v-card>
    <div class="tools-grid mt-6">
      <v-card v-for="tool in tools" :key="tool.id" class="tool-card mb-4" :elevation="editToolId === tool.id ? 3 : 1">
        <v-card-item class="pb-2">
          <template v-slot:prepend>
            <v-chip color="primary" class="tool-id mr-2" size="small">ID: {{ tool.id }}</v-chip>
            <v-switch
              v-if="editToolId === tool.id"
              v-model="editTool.active"
              inset
              hide-details
              class="ml-2"
              color="success"
            ></v-switch>
            <v-chip
              v-else
              :color="tool.active ? 'success' : 'error'"
              class="ml-2"
              size="small"
            >
              {{ tool.active ? 'Active' : 'Inactive' }}
            </v-chip>
          </template>
        </v-card-item>

        <v-card-text class="pt-2">
          <v-row dense>
            <v-col cols="12" md="6">
              <div class="text-subtitle-2 mb-1 text-medium-emphasis">Name</div>
              <v-text-field
                v-if="editToolId === tool.id"
                v-model="editTool.name"
                density="comfortable"
                variant="outlined"
                hide-details
              ></v-text-field>
              <div v-else class="text-h6">{{ tool.name }}</div>
            </v-col>
            
            <v-col cols="12" md="6">
              <div class="text-subtitle-2 mb-1 text-medium-emphasis">Location</div>
              <v-select
                v-if="editToolId === tool.id"
                v-model="editTool.run_location"
                :items="locationOptions"
                density="comfortable"
                variant="outlined"
                hide-details
              ></v-select>
              <div v-else class="text-body-1">{{ tool.run_location }}</div>
            </v-col>
            
            <v-col cols="12" class="mt-3">
              <div class="text-subtitle-2 mb-1 text-medium-emphasis">Description</div>
              <v-text-field
                v-if="editToolId === tool.id"
                v-model="editTool.description"
                density="comfortable"
                variant="outlined"
                hide-details
              ></v-text-field>
              <div v-else class="text-body-1">{{ tool.description }}</div>
            </v-col>
            
            <v-col cols="12" class="mt-3">
              <div class="text-subtitle-2 mb-1 text-medium-emphasis">SQL Query</div>
              <v-textarea
                v-if="editToolId === tool.id"
                v-model="editTool.sql_query"
                density="comfortable"
                variant="outlined"
                rows="3"
                hide-details
              ></v-textarea>
              <v-sheet
                v-else
                color="grey-lighten-4"
                class="pa-3 rounded"
              >
                <pre class="text-body-2">{{ tool.sql_query }}</pre>
              </v-sheet>
            </v-col>

            <v-col cols="12" class="mt-3">
              <v-expansion-panels>
                <v-expansion-panel>
                  <v-expansion-panel-title>
                    Parameters
                    <template v-slot:actions>
                      <v-chip
                        v-if="tool.parameters?.length"
                        size="x-small"
                        color="primary"
                        class="ml-2"
                      >
                        {{ tool.parameters.length }}
                      </v-chip>
                    </template>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <template v-if="editToolId === tool.id">
                      <div class="d-flex justify-end mb-3">
                        <v-btn
                          color="primary"
                          size="small"
                          @click="addParameter(tool.id)"
                          prepend-icon="mdi-plus"
                        >
                          Add Parameter
                        </v-btn>
                      </div>
                      <div v-for="(param, index) in editTool.parameters || []" :key="index">
                        <v-card variant="outlined" class="pa-3 mb-2">
                          <v-row dense>
                            <v-col cols="12" md="4">
                              <v-text-field
                                v-model="param.name"
                                label="Name*"
                                density="comfortable"
                                variant="outlined"
                                hide-details
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-select
                                v-model="param.datatype"
                                :items="datatypeOptions"
                                label="Data Type*"
                                density="comfortable"
                                variant="outlined"
                                hide-details
                              ></v-select>
                            </v-col>
                            <v-col cols="12" md="4" class="d-flex align-center">
                              <v-text-field
                                v-model="param.description"
                                label="Description"
                                density="comfortable"
                                variant="outlined"
                                hide-details
                              ></v-text-field>
                              <v-btn
                                icon="mdi-delete"
                                color="error"
                                size="small"
                                variant="text"
                                class="ml-2 shrink"
                                @click="removeParameter(tool.id, index)"
                              ></v-btn>
                            </v-col>
                          </v-row>
                        </v-card>
                      </div>
                    </template>
                    <v-list v-else-if="tool.parameters?.length" density="comfortable">
                      <v-list-item v-for="(param, index) in tool.parameters" :key="index">
                        <template v-slot:prepend>
                          <v-icon icon="mdi-code-brackets" size="small"></v-icon>
                        </template>
                        <v-list-item-title>
                          <span class="font-weight-medium">{{ param.name }}</span>
                          <span class="text-medium-emphasis"> ({{ param.datatype }})</span>
                        </v-list-item-title>
                        <v-list-item-subtitle v-if="param.description" class="text-caption">
                          {{ param.description }}
                        </v-list-item-subtitle>
                      </v-list-item>
                    </v-list>
                    <div v-else class="text-center py-3 text-medium-emphasis">
                      No parameters defined
                    </div>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <template v-if="editToolId !== tool.id">
            <v-btn
              color="primary"
              variant="outlined"
              prepend-icon="mdi-pencil"
              @click="startEditTool(tool)"
            >
              Edit
            </v-btn>
          </template>
          <template v-else>
            <v-btn
              color="error"
              variant="outlined"
              prepend-icon="mdi-delete"
              class="mr-2"
              @click="deleteToolEdit(tool.id)"
            >
              Delete
            </v-btn>
            <v-btn
              color="secondary"
              variant="outlined"
              class="mr-2"
              @click="cancelToolEdit"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              prepend-icon="mdi-check"
              @click="saveToolEdit"
            >
              Save
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </div>
  </div>
</template>

<script>
import aiService from "@/services/aiService";

export default {
  name: "Prompt",
  data() {
    return {
      purposeOptions: [],
      selectedPurpose: null,
      clientOptions: [],
      selectedClientId: null,
      editablePrompt: "",
      promptMap: {},
      tools: [],
      editToolId: null,
      showErrorDialog: false,
      editTool: {},
      newTool: {
        name: "",
        description: "",
        sql_query: "",
        active: true,
        agent: "",
        run_location: "DSS",
        parameters: []
      },
      locationOptions: ["DSS", "OD"],
      datatypeOptions: ["string", "number", "integer", "boolean", "array"],
      toolErrorMessage: "",
      purposeData: {}, // Will store the complete response from getPromptTools
    };
  },
  async created() {
    try {
      const response = await aiService.getPromptTools();
      this.purposeData = response.data;
      this.purposeOptions = Object.keys(this.purposeData);
      
      if (this.purposeOptions.length > 0) {
        this.selectedPurpose = this.purposeOptions[0];
        this.updatePurposeData();
      }
    } catch (err) {
      console.error("Failed to fetch AI prompt data:", err);
      this.purposeOptions = [];
    }
  },
  watch: {
    selectedPurpose(newPurpose) {
      if (newPurpose) {
        this.updatePurposeData();
      }
    }
  },
  methods: {
    updatePurposeData() {
      if (!this.selectedPurpose) return;
      
      const purposeData = this.purposeData[this.selectedPurpose];
      this.clientOptions = purposeData.prompts;
      this.tools = purposeData.tools;
      
      // Reset prompt map
      this.promptMap = {};
      this.clientOptions.forEach((item) => {
        this.promptMap[item.client_id] = item.prompt || "";
      });
      
      // Select first client if available
      if (this.clientOptions.length > 0) {
        this.selectedClientId = this.clientOptions[0].client_id;
        this.editablePrompt = this.promptMap[this.selectedClientId];
      }
    },
    onClientChange(newClientId) {
      this.editablePrompt = this.promptMap[newClientId] || "";
      this.selectedClientId = newClientId;
    },
    async savePrompt() {
      console.log(
        "Saved prompt for client:",
        this.selectedClientId,
        this.editablePrompt
      );
      await aiService.updateAiPrompt({
        client_id: this.selectedClientId,
        agent: this.selectedPurpose,
        prompt: this.editablePrompt,
      });
    },
    startEditTool(tool) {
      this.editToolId = tool.id;
      this.editTool = {
        ...tool,
        parameters: tool.parameters ? [...tool.parameters] : []
      };
    },
    showError(message) {
      this.toolErrorMessage = message;
      this.showErrorDialog = true;
    },

    async saveToolEdit() {
      this.toolErrorMessage = "";
      this.editTool.agent = this.selectedPurpose;

      // Validate parameters if they exist and have values
      if (this.editTool.parameters?.length) {
        const nonEmptyParams = this.editTool.parameters.filter(p => p.name || p.datatype || p.description);
        for (const param of nonEmptyParams) {
          if (!param.name || !param.datatype) {
            this.showError("For each defined parameter, both name and datatype are required");
            return;
          }
        }
      }

      try {
        const validate = await aiService.validateSqlQuery({query:this.editTool.sql_query, run_location:this.editTool.run_location});
        const validation = validate.data;
        if (!validation.isValid) {
          this.showError(validation.message);
          return;
        }
        await aiService.updateAiTool(this.editTool);
        const idx = this.tools.findIndex(t => t.id === this.editToolId);
        if (idx !== -1) this.tools[idx] = { ...this.editTool };
        this.editToolId = null;
        this.editTool = {};
      } catch (error) {
        this.showError(error.message || "Failed to save tool");
      }
    },

    addParameter(toolId) {
      if (!this.editTool.parameters) {
        this.editTool.parameters = [];
      }
      this.editTool.parameters.push({
        name: "",
        datatype: "string",
        description: null
      });
    },

    removeParameter(toolId, index) {
      this.editTool.parameters.splice(index, 1);
    },
    cancelToolEdit() {
      this.editToolId = null;
      this.editTool = {};
    },
    addNewToolParameter() {
      if (!this.newTool.parameters) {
        this.newTool.parameters = [];
      }
      this.newTool.parameters.push({
        name: "",
        datatype: "string",
        description: null
      });
    },

    removeNewToolParameter(index) {
      this.newTool.parameters.splice(index, 1);
    },

    resetNewTool() {
      this.newTool = {
        name: "",
        description: "",
        sql_query: "",
        active: true,
        agent: "",
        run_location: "DSS",
        parameters: []
      };
    },

    async createTool() {
      this.toolErrorMessage = "";
      this.newTool.agent = this.selectedPurpose;

      // Validate parameters if they exist and have values
      if (this.newTool.parameters?.length) {
        const nonEmptyParams = this.newTool.parameters.filter(p => p.name || p.datatype || p.description);
        for (const param of nonEmptyParams) {
          if (!param.name || !param.datatype) {
            this.showError("For each defined parameter, both name and datatype are required");
            return;
          }
        }
        // Clean up empty parameters before saving
        this.newTool.parameters = nonEmptyParams;
      }

      try {
        const validate = await aiService.validateSqlQuery({
          query: this.newTool.sql_query,
          run_location: this.newTool.run_location,
          parameters: this.newTool.parameters
        });
        const validation = validate.data;
        if (!validation.isValid) {
          this.showError(validation.message);
          return;
        }
        
        await aiService.updateAiTool(this.newTool);
        const newId = Math.max(0, ...this.tools.map(t => t.id)) + 1;
        this.tools.push({ ...this.newTool, id: newId });
        this.resetNewTool();
      } catch (error) {
        this.showError(error.message || "Failed to create tool");
      }
    },
    async deleteToolEdit(id) {
      try {
        await aiService.deleteQuery(id);
        this.tools = this.tools.filter(tool => tool.id !== id);
        this.editToolId = null;
        this.editTool = {};
      } catch (error) {
        console.error("Failed to delete tool:", error);
        this.showError("Failed to delete tool: " + (error.message || "Unknown error occurred"));
      }
    }
  },
};
</script>

<style scoped>
.prompt-container {
  max-width: 1400px;
  margin: 2rem auto;
  padding: 2rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
}

.prompt-title {
  margin-bottom: 2rem;
  font-size: 2rem;
  font-weight: 600;
  text-align: center;
}

.prompt-select {
  margin-bottom: 1.5rem;
}

.prompt-textarea {
  margin-bottom: 1.5rem;
}

.prompt-save-btn {
  align-self: flex-end;
}

.tools-title {
  margin-top: 2.5rem;
  margin-bottom: 1rem;
  font-size: 1.5rem;
  font-weight: 500;
  color: var(--v-primary-base);
}

.tool-error-message {
  color: #d32f2f;
  background: #fff0f0;
  border: 1px solid #d32f2f;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
  font-weight: 500;
}

.tools-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.tool-card {
  border: 1px solid rgba(0, 0, 0, 0.12);
  transition: all 0.2s ease-in-out;
}

.tool-card:hover {
  border-color: var(--v-primary-base);
}

.tool-id {
  font-weight: 500;
  letter-spacing: 0.5px;
}

pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: 'Roboto Mono', monospace;
}

/* Consistent field spacings */
.v-text-field,
.v-select,
.v-textarea {
  margin-bottom: 0.5rem;
}

/* Parameter cards consistency */
.v-card.v-card--variant-outlined {
  border-color: rgba(0, 0, 0, 0.12);
}

/* Better expansion panel styling */
.v-expansion-panel-title {
  min-height: 48px;
}

.v-expansion-panel-text__wrapper {
  padding: 1rem;
}

/* List item spacing */
.v-list-item {
  min-height: 40px;
  padding: 0.5rem 1rem;
}
</style>